/**
 * How the dream list is organised (Dreams UX pass 2026-09-06). A dream list
 * is read for "what am I moving toward, and is anything moving", so the
 * default grouping is by movement — and a dream's movement is its goals':
 *
 *   Ready to check    the journey is complete, or the date you set has passed — answer it
 *   In motion         a goal under it has a next thing on the calendar, something got done
 *                     under it in the last fourteen days, or it got done today
 *   Needs a journey   live, nothing under it, no journey — the next move is to plan one
 *   Waiting for you   has goals or a journey, but nothing is moving under it
 *   Paused            paused by the person
 *   Done              done, and let go — collapsed
 *
 * One status axis reduces to words with a precedence: Done › Let go › Paused ›
 * Parked › nothing. Parked is rationing's word, not the person's choice: it
 * shows as a tag but the dream stays live (the same call as goals and skills).
 *
 * Pure functions with no runtime imports so node can probe them
 * (`npm run test:dreams`); the page injects the life-area labeller.
 */
export type DreamSectionKey = 'check' | 'moving' | 'start' | 'waiting' | 'paused' | 'done'
export type DreamGroupBy = 'movement' | 'area' | 'none'
export type DreamSortBy = 'attention' | 'progress' | 'created' | 'updated'
export type SortDir = 'asc' | 'desc'
export interface SortSpec { by: DreamSortBy; dir: SortDir }

export interface DreamLike {
  id: number | string
  title: string
  status?: string | null
  progress?: number | null
  target_date?: string | null
  completed_at?: string | null
  let_go_at?: string | null
  created_at?: string | null
  updated_at?: string | null
  life_area?: string | null
  life_areas?: string[] | null
}

export interface DreamCardLike {
  path?: { status: string; done: number; total: number; current?: unknown } | null
  next?: { kind?: string | null; date?: string | null } | null
  today?: unknown | null
  days_active?: number
  window_days?: number
  window_capped?: boolean
  days_total?: number
  last_date?: string | null
  open_goals?: number
  goals?: unknown[] | null
  derived_progress?: number | null
}

export interface DreamGroup<D extends DreamLike = DreamLike> {
  key: string
  title: string
  quiet?: string
  items: D[]
  collapsedByDefault?: boolean
}

export const SECTION_TITLE: Record<DreamSectionKey, string> = {
  check: 'Ready to check',
  moving: 'In motion',
  start: 'Needs a journey',
  waiting: 'Waiting for you',
  paused: 'Paused',
  done: 'Done',
}
const SECTION_ORDER: DreamSectionKey[] = ['check', 'moving', 'start', 'waiting', 'paused', 'done']

// ── one status axis, one line of words ──

export type DreamStateKey = 'done' | 'let_go' | 'paused' | 'parked' | 'active'
export interface DreamState { key: DreamStateKey; words: string | null }

export function dreamState(d: DreamLike): DreamState {
  const s = String(d.status || '').toLowerCase()
  if (s === 'completed') return { key: 'done', words: 'Done' }
  if (s === 'cancelled' || s === 'dropped' || s === 'lapsed') return { key: 'let_go', words: 'Let go' }
  if (s === 'paused' || s === 'paused_by_user' || s === 'hold') return { key: 'paused', words: 'Paused' }
  if (s === 'parked') return { key: 'parked', words: 'Parked' }
  return { key: 'active', words: null }
}

export const isFinished = (d: DreamLike): boolean => { const k = dreamState(d).key; return k === 'done' || k === 'let_go' }
export const isResting = (d: DreamLike): boolean => dreamState(d).key === 'paused'

/** Whole local days from one YYYY-MM-DD to another; null when unreadable. */
export function daysBetween(fromKey: string, toKey: string): number | null {
  const a = /^(\d{4})-(\d{2})-(\d{2})/.exec(fromKey), b = /^(\d{4})-(\d{2})-(\d{2})/.exec(toKey)
  if (!a || !b) return null
  const da = new Date(Number(a[1]), Number(a[2]) - 1, Number(a[3]))
  const db = new Date(Number(b[1]), Number(b[2]) - 1, Number(b[3]))
  return Math.round((db.getTime() - da.getTime()) / 86_400_000)
}

export function targetPassed(d: DreamLike, todayKey: string): boolean {
  if (!d.target_date) return false
  const n = daysBetween(todayKey, d.target_date)
  return n != null && n < 0
}

/** The progress the person sees: the goals' when there are goals, else the dream's own figure. */
export function shownProgress(d: DreamLike, card: DreamCardLike | null | undefined): number {
  const derived = card?.derived_progress
  if (derived != null && (card?.goals?.length || 0) > 0) return Math.round(Number(derived))
  return Math.round(Number(d.progress || 0))
}

/** Anything under the dream at all — a goal, a journey, or something once done. */
export function hasAnything(card: DreamCardLike | null | undefined): boolean {
  if (!card) return false
  if (card.path) return true
  return Boolean((card.goals?.length || 0) || card.days_total)
}

/** A next thing that is really moving: a goal's own next (a task, habit or skill), not a goal still waiting for one. */
export function nextIsMoving(card: DreamCardLike | null | undefined): boolean {
  const n = card?.next
  if (!n) return false
  return n.kind !== 'goal' || Boolean(n.date)
}

export function sectionOf(d: DreamLike, card: DreamCardLike | null | undefined, todayKey: string): DreamSectionKey {
  const state = dreamState(d)
  if (state.key === 'done' || state.key === 'let_go') return 'done'
  if (state.key === 'paused') return 'paused'
  if (card?.path?.status === 'completed' || targetPassed(d, todayKey)) return 'check'
  if (card?.today || nextIsMoving(card)) return 'moving'
  if (card?.last_date) {
    const n = daysBetween(card.last_date, todayKey)
    if (n != null && n <= 14) return 'moving'
  }
  if (!hasAnything(card)) return 'start'
  return 'waiting'
}

const cmp = (a: string | number, b: string | number) => (a < b ? -1 : a > b ? 1 : 0)
const title = (d: DreamLike) => d.title.toLowerCase()

export function sortSection<D extends DreamLike>(items: D[], key: DreamSectionKey, cards: Record<string, DreamCardLike | undefined>): D[] {
  const card = (d: D) => cards[String(d.id)]
  const arr = [...items]
  switch (key) {
    case 'moving':
      return arr.sort((a, b) =>
        cmp(card(a)?.today ? 0 : 1, card(b)?.today ? 0 : 1)
        || cmp(String(card(a)?.next?.date || '9999'), String(card(b)?.next?.date || '9999'))
        || cmp(String(card(b)?.last_date || ''), String(card(a)?.last_date || ''))
        || cmp(title(a), title(b)))
    case 'waiting':
      return arr.sort((a, b) => cmp(String(card(b)?.last_date || ''), String(card(a)?.last_date || '')) || cmp(shownProgress(b, card(b)), shownProgress(a, card(a))) || cmp(title(a), title(b)))
    case 'start':
      return arr.sort((a, b) => cmp(String(b.created_at || ''), String(a.created_at || '')))
    case 'done':
      return arr.sort((a, b) => cmp(String(b.completed_at || b.let_go_at || b.updated_at || ''), String(a.completed_at || a.let_go_at || a.updated_at || '')))
    default:
      return arr.sort((a, b) => cmp(title(a), title(b)))
  }
}

export function timeKey(d: DreamLike, by: 'created' | 'updated'): string {
  return String((by === 'created' ? (d.created_at || d.updated_at) : (d.updated_at || d.created_at)) || '')
}

export function sortByTime<D extends DreamLike>(items: D[], by: 'created' | 'updated', dir: SortDir): D[] {
  return [...items].sort((a, b) => {
    const ka = timeKey(a, by), kb = timeKey(b, by)
    if (!ka || !kb) return cmp(ka ? 0 : 1, kb ? 0 : 1)
    return dir === 'desc' ? cmp(kb, ka) : cmp(ka, kb)
  })
}

export function sortByProgress<D extends DreamLike>(items: D[], cards: Record<string, DreamCardLike | undefined>, dir: SortDir): D[] {
  const p = (d: D) => shownProgress(d, cards[String(d.id)])
  return [...items].sort((a, b) => (dir === 'desc' ? cmp(p(b), p(a)) : cmp(p(a), p(b))) || cmp(title(a), title(b)))
}

export function matchesQuery(d: DreamLike & { description?: string | null }, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const hay = [d.title, d.description, d.life_area, ...(d.life_areas || [])].filter(Boolean).join(' ').toLowerCase()
  return q.split(/\s+/).every((w) => hay.includes(w))
}

export interface DreamSummary { total: number; open: number; moving: number; check: number; start: number; doneWeek: number; resting: number; done: number }

export function summarize(dreams: DreamLike[], cards: Record<string, DreamCardLike | undefined>, todayKey: string): DreamSummary {
  const s: DreamSummary = { total: 0, open: 0, moving: 0, check: 0, start: 0, doneWeek: 0, resting: 0, done: 0 }
  for (const d of dreams) {
    s.total += 1
    const k = sectionOf(d, cards[String(d.id)], todayKey)
    if (k === 'done') s.done += 1; else s.open += 1
    if (k === 'moving') s.moving += 1
    if (k === 'check') s.check += 1
    if (k === 'start') s.start += 1
    if (k === 'paused') s.resting += 1
    const last = cards[String(d.id)]?.last_date
    if (last && k !== 'done') { const n = daysBetween(last, todayKey); if (n != null && n >= 0 && n <= 6) s.doneWeek += 1 }
  }
  return s
}

export function summaryWords(s: DreamSummary): string {
  if (s.total === 0) return 'Nothing here yet'
  const parts = [`${s.open} open`]
  if (s.check) parts.push(`${s.check} ready to check`)
  if (s.moving) parts.push(`${s.moving} in motion`)
  if (s.doneWeek) parts.push(`${s.doneWeek} with something done this week`)
  // The section is "Needs a journey" (North Star rule 2: one fact, one expression).
  if (s.start) parts.push(`${s.start} need${s.start === 1 ? 's' : ''} a journey`)
  if (s.resting) parts.push(`${s.resting} paused`)
  if (s.done) parts.push(`${s.done} done`)
  return parts.join(' · ')
}

const attentionOrder = <D extends DreamLike>(cards: Record<string, DreamCardLike | undefined>, todayKey: string) => (a: D, b: D) => {
  const ka = sectionOf(a, cards[String(a.id)], todayKey), kb = sectionOf(b, cards[String(b.id)], todayKey)
  if (ka !== kb) return cmp(SECTION_ORDER.indexOf(ka), SECTION_ORDER.indexOf(kb))
  return sortSection([a, b], ka, cards)[0] === a ? -1 : 1
}

export function groupDreams<D extends DreamLike>(
  dreams: D[], cards: Record<string, DreamCardLike | undefined>, by: DreamGroupBy, todayKey: string,
  labelArea: (value: string) => string = (v) => v, sort: SortSpec = { by: 'attention', dir: 'desc' },
): DreamGroup<D>[] {
  const groups = buildGroups(dreams, cards, by, todayKey, labelArea)
  if (sort.by === 'attention') return groups
  if (sort.by === 'progress') return groups.map((g) => ({ ...g, items: sortByProgress(g.items, cards, sort.dir) }))
  return groups.map((g) => ({ ...g, items: sortByTime(g.items, sort.by as 'created' | 'updated', sort.dir) }))
}

function buildGroups<D extends DreamLike>(dreams: D[], cards: Record<string, DreamCardLike | undefined>, by: DreamGroupBy, todayKey: string, labelArea: (v: string) => string): DreamGroup<D>[] {
  if (by === 'movement') {
    const buckets: Record<DreamSectionKey, D[]> = { check: [], moving: [], start: [], waiting: [], paused: [], done: [] }
    for (const d of dreams) buckets[sectionOf(d, cards[String(d.id)], todayKey)].push(d)
    const anyLive = (['check', 'start', 'waiting'] as DreamSectionKey[]).some((k) => buckets[k].length > 0)
    return SECTION_ORDER
      .map((k): DreamGroup<D> => ({
        key: k, title: SECTION_TITLE[k], items: sortSection(buckets[k], k, cards),
        quiet: k === 'moving' && buckets.moving.length === 0 && anyLive ? 'Nothing in motion right now. Plan a journey on one, or give one of its goals a next step.' : undefined,
        collapsedByDefault: k === 'done',
      }))
      .filter((g) => g.items.length > 0 || g.quiet)
  }
  const live = dreams.filter((d) => !isFinished(d))
  const finished = sortSection(dreams.filter(isFinished), 'done', cards)
  if (by === 'none') {
    const groups: DreamGroup<D>[] = []
    if (live.length) groups.push({ key: 'all', title: 'Dreams', items: [...live].sort(attentionOrder(cards, todayKey)) })
    if (finished.length) groups.push({ key: 'done', title: SECTION_TITLE.done, items: finished, collapsedByDefault: true })
    return groups
  }
  const map = new Map<string, DreamGroup<D>>()
  const orphanKey = 'area:none'
  for (const d of live) {
    const area = d.life_area || (d.life_areas && d.life_areas[0]) || null
    const key = area ? `area:${area}` : orphanKey
    const t = area ? labelArea(area) : 'No life area'
    if (!map.has(key)) map.set(key, { key, title: t, items: [] })
    map.get(key)!.items.push(d)
  }
  const groups = [...map.values()]
  for (const g of groups) g.items.sort(attentionOrder(cards, todayKey))
  groups.sort((a, b) => cmp(a.key === orphanKey ? 1 : 0, b.key === orphanKey ? 1 : 0) || cmp(a.title.toLowerCase(), b.title.toLowerCase()))
  if (finished.length) groups.push({ key: 'done', title: SECTION_TITLE.done, items: finished, collapsedByDefault: true })
  return groups
}
